import { AxiosResponse } from 'axios';
import {
  DashboardSubscription,
  DashboardSubscriptionRequest,
  SendNowResult,
  SubscribableDashboard,
  SubscriptionRun,
  SubscriptionStatus,
} from '@/types/DashboardSubscription';
import { Service } from './api';

const basePath = '/v2/dashboard-subscriptions';

class DashboardSubscriptionService extends Service {
  async list(): Promise<DashboardSubscription[]> {
    return this.handleRequest(this.get<DashboardSubscription[]>(basePath));
  }

  async dashboards(): Promise<SubscribableDashboard[]> {
    return this.handleRequest(this.get<SubscribableDashboard[]>(`${basePath}/dashboards`));
  }

  async dashboard(accountTableauId: number | string): Promise<SubscribableDashboard> {
    return this.handleRequest(this.get<SubscribableDashboard>(`${basePath}/dashboards/${accountTableauId}`));
  }

  async create(request: DashboardSubscriptionRequest): Promise<void> {
    await this.handleRequest(this.post<void>(basePath, request));
  }

  async update(id: number, request: DashboardSubscriptionRequest): Promise<void> {
    await this.handleRequest(this.put<void>(`${basePath}/${id}`, request));
  }

  async setStatus(id: number, status: SubscriptionStatus): Promise<void> {
    await this.handleRequest(this.patch<void>(`${basePath}/${id}/status`, { status }));
  }

  async remove(id: number): Promise<void> {
    await this.handleRequest(this.delete<void>(`${basePath}/${id}`));
  }

  async runs(id: number, limit?: number): Promise<SubscriptionRun[]> {
    return this.handleRequest(this.get<SubscriptionRun[]>(`${basePath}/${id}/runs`, limit ? { limit } : undefined));
  }

  async run(id: number, runId: number): Promise<SubscriptionRun> {
    return this.handleRequest(this.get<SubscriptionRun>(`${basePath}/${id}/runs/${runId}`));
  }

  async sendNow(id: number): Promise<SendNowResult> {
    return this.handleRequest(this.post<SendNowResult>(`${basePath}/${id}/send`, undefined));
  }
}

// handleResponse keeps the response as the Error's cause, which is the only place the status survives.
export function isConflictError(error: unknown): boolean {
  return error instanceof Error && (error.cause as AxiosResponse | undefined)?.status === 409;
}

export const dashboardSubscriptionService = new DashboardSubscriptionService();
